import { Icon20InfoCircleOutline } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { subscriptionInformerEnabled, subsInformerStorageKey } from 'reactApp/appHelpers/featuresHelpers/features/subscriptionsInformer';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { Button } from 'reactApp/ui/Button/Button';
import { Informer } from 'reactApp/ui/Informer/Informer';
import { EInformerColor } from 'reactApp/ui/Informer/Informer.types';
import { formatSubscriptionData } from 'reactApp/utils/formatDate';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './HasSubscriptionInformer.css';

interface HasSubscriptionInformerProps {
    subscription: BillingDataSubscriptionInfo;
}

export const HasSubscriptionInformer = memo(({ subscription }: HasSubscriptionInformerProps): ReactElement | null => {
    const dispatch = useDispatch();
    const { goToSubscriptions } = useGoToSubscriptions();

    const [showInformer, setShowInformer] = useState<boolean>(true);

    const sendAnalytics = useCallback((action) => {
        sendPaymentGa({
            eventCategory: ECategoryGa.banner,
            action,
            name: 'mnz_subs_ends_soon',
        });
    }, []);

    const onClose = useCallback(() => {
        sendAnalytics('close');
        storeHelper.markAsShown(subsInformerStorageKey);
        setShowInformer(false);
    }, [sendAnalytics]);

    const onClick = useCallback(() => {
        sendAnalytics('click');
        storeHelper.markAsShown(subsInformerStorageKey);
        setShowInformer(false);

        if (subscriptionInformerEnabled === 'c') {
            goToSubscriptions({
                isNewTariffsPage: true,
                query: PaymentUTM.subsEndsSoonBanner,
            });
            return;
        }

        dispatch(SubscriptionRenewalActions.turnOnAutopayment({ subscription }));
    }, [goToSubscriptions, subscription, sendAnalytics]);

    const button = useMemo(
        () => (
            <Button theme="vk" className={styles.button} onClick={onClick}>
                <Text weight="2"> Продлить подписку</Text>
            </Button>
        ),
        []
    );

    useEffect(() => {
        sendAnalytics('view');
    }, []);

    if (!showInformer) {
        return null;
    }

    return (
        <div className={styles.root}>
            <Informer
                dataQaId="informer-trash-overq"
                color={EInformerColor.base}
                icon={<Icon20InfoCircleOutline fill="var(--vkui--color_icon_accent)" />}
                button={button}
                closable={subscriptionInformerEnabled !== 'c'}
                onClose={onClose}
            >
                <div>
                    <Text weight="2">
                        {formatSubscriptionData(subscription.expires)} отключится подписка Mail&nbsp;Space&nbsp;— ещё&nbsp;есть&nbsp;время
                        её&nbsp;продлить
                    </Text>
                    <Spacing size={4} />
                    <div className={styles.text}>
                        У&nbsp;вас&nbsp;по‑прежнему не&nbsp;будет рекламы даже&nbsp;в&nbsp;Почте, а&nbsp;в&nbsp;Облако можно будет загружать
                        тяжёлые файлы
                    </div>
                </div>
            </Informer>
        </div>
    );
});

HasSubscriptionInformer.displayName = 'HasSubscriptionInformer';
